import { POLLINATOR_GARDEN_REQUIRED_ASSETS } from './pollinator-garden/PollinatorGardenAssets.js';

export interface DiscoveryLabImageAssetDefinition {
  readonly key: string;
  readonly path: string;
  readonly kind: 'image';
  readonly runtimeReady: true;
}

const image = (key: string, path: string): DiscoveryLabImageAssetDefinition => ({
  key,
  path,
  kind: 'image',
  runtimeReady: true,
});

/** Approved runtime files imported from the three-world production-art delivery. */
export const DISCOVERY_LAB_ASSETS = {
  arrivalBackground: image(
    'discovery-lab-arrival-background',
    'assets/discovery-lab/backgrounds/discovery-lab-arrival-bg.png',
  ),
  fieldCampusHub: image(
    'discovery-lab-field-campus-hub-v2',
    'assets/discovery-lab/hub/backgrounds/field-campus-hub-v2.png',
  ),
  campusForeground: image(
    'discovery-lab-campus-foreground-foliage',
    'assets/discovery-lab/hub/foreground/campus-foreground-foliage.png',
  ),
  destinationSignpost: image(
    'discovery-lab-destination-signpost',
    'assets/discovery-lab/hub/ui/destination-signpost.png',
  ),
  fieldJournalPlaque: image(
    'discovery-lab-field-journal-plaque',
    'assets/discovery-lab/hub/ui/field-journal-plaque.png',
  ),
  tutorialFieldNote: image(
    'discovery-lab-tutorial-field-note',
    'assets/discovery-lab/hub/ui/tutorial-field-note.png',
  ),
  destinationMarker: image(
    'discovery-lab-destination-marker',
    'assets/discovery-lab/hub/ui/destination-marker.png',
  ),
  cosmoContactShadow: image(
    'discovery-lab-cosmo-contact-shadow',
    'assets/discovery-lab/hub/characters/cosmo-contact-shadow.png',
  ),
  washedOutTrail: image(
    'discovery-lab-washed-out-trail',
    'assets/discovery-lab/environments/washed-out-trail.png',
  ),
  erosionStation: image(
    'discovery-lab-erosion-station',
    'assets/discovery-lab/environments/erosion-station.png',
  ),
  waterSample: image(
    'discovery-lab-water-sample',
    'assets/discovery-lab/interactives/water-sample.png',
  ),
  soilSample: image(
    'discovery-lab-soil-sample',
    'assets/discovery-lab/interactives/soil-sample.png',
  ),
  siftTray: image(
    'discovery-lab-sift-tray',
    'assets/discovery-lab/interactives/sift-tray.png',
  ),
  magnifierTool: image(
    'discovery-lab-magnifier-tool',
    'assets/discovery-lab/interactives/magnifier-tool.png',
  ),
  probeTool: image(
    'discovery-lab-probe-tool',
    'assets/discovery-lab/interactives/probe-tool.png',
  ),
  trailWashout: image(
    'discovery-lab-trail-washout',
    'assets/discovery-lab/trail-states/trail-washout.png',
  ),
  trailPartial: image(
    'discovery-lab-trail-partial',
    'assets/discovery-lab/trail-states/trail-partial.png',
  ),
  trailDry: image(
    'discovery-lab-trail-dry',
    'assets/discovery-lab/trail-states/trail-dry.png',
  ),
  trailErosion: image(
    'discovery-lab-trail-erosion',
    'assets/discovery-lab/trail-states/trail-erosion.png',
  ),
  trailRestored: image(
    'discovery-lab-trail-restored',
    'assets/discovery-lab/trail-states/trail-restored.png',
  ),
  trailCracked: image(
    'discovery-lab-trail-cracked',
    'assets/discovery-lab/trail-states/trail-cracked.png',
  ),
  labMissionPanel: image(
    'discovery-lab-lab-mission-panel',
    'assets/discovery-lab/ui/lab-mission-panel.png',
  ),
  erosionParticles: image(
    'discovery-lab-erosion-particles',
    'assets/discovery-lab/vfx/erosion-particles.png',
  ),
  restorationVfx: image(
    'discovery-lab-restoration-vfx',
    'assets/discovery-lab/vfx/restoration-vfx.png',
  ),
  waterSplashVfx: image(
    'discovery-lab-water-splash-vfx',
    'assets/discovery-lab/vfx/water-splash-vfx.png',
  ),
  bubbleSparkleVfx: image(
    'discovery-lab-bubble-sparkle-vfx',
    'assets/discovery-lab/vfx/bubble-sparkle-vfx.png',
  ),
  cosmo: image(
    'discovery-lab-cosmo',
    'assets/shared/cosmo/cosmo-idle.png',
  ),
} as const;

export const DISCOVERY_LAB_ASSET_LIST = Object.values(DISCOVERY_LAB_ASSETS);
export const DISCOVERY_LAB_RUNTIME_ASSET_LIST = [
  ...DISCOVERY_LAB_ASSET_LIST,
  ...POLLINATOR_GARDEN_REQUIRED_ASSETS,
] as const;

/** Every texture needed to enter and finish the first production investigation. */
export const DISCOVERY_LAB_OPENING_REQUIRED_ASSETS = [
  DISCOVERY_LAB_ASSETS.fieldCampusHub,
  DISCOVERY_LAB_ASSETS.campusForeground,
  DISCOVERY_LAB_ASSETS.destinationSignpost,
  DISCOVERY_LAB_ASSETS.fieldJournalPlaque,
  DISCOVERY_LAB_ASSETS.tutorialFieldNote,
  DISCOVERY_LAB_ASSETS.destinationMarker,
  DISCOVERY_LAB_ASSETS.cosmoContactShadow,
  DISCOVERY_LAB_ASSETS.washedOutTrail,
  DISCOVERY_LAB_ASSETS.erosionStation,
  DISCOVERY_LAB_ASSETS.waterSample,
  DISCOVERY_LAB_ASSETS.siftTray,
  DISCOVERY_LAB_ASSETS.trailWashout,
  DISCOVERY_LAB_ASSETS.trailPartial,
  DISCOVERY_LAB_ASSETS.trailDry,
  DISCOVERY_LAB_ASSETS.trailErosion,
  DISCOVERY_LAB_ASSETS.trailRestored,
  DISCOVERY_LAB_ASSETS.labMissionPanel,
  DISCOVERY_LAB_ASSETS.erosionParticles,
  DISCOVERY_LAB_ASSETS.restorationVfx,
  DISCOVERY_LAB_ASSETS.waterSplashVfx,
  DISCOVERY_LAB_ASSETS.bubbleSparkleVfx,
  DISCOVERY_LAB_ASSETS.cosmo,
] as const;

export function preloadDiscoveryLabAssets(scene: Phaser.Scene) {
  DISCOVERY_LAB_RUNTIME_ASSET_LIST.forEach(asset => scene.load.image(asset.key, asset.path));
}

export function getMissingDiscoveryLabOpeningAssets(
  source: Phaser.Scene | Phaser.Textures.TextureManager,
) {
  const textures = 'textures' in source ? source.textures : source;
  return DISCOVERY_LAB_OPENING_REQUIRED_ASSETS.filter(asset => !textures.exists(asset.key));
}

export function getMissingDiscoveryLabRuntimeAssets(
  source: Phaser.Scene | Phaser.Textures.TextureManager,
) {
  const textures = 'textures' in source ? source.textures : source;
  return DISCOVERY_LAB_RUNTIME_ASSET_LIST.filter(asset => !textures.exists(asset.key));
}

export function isDiscoveryLabOpeningReady(textures: Phaser.Textures.TextureManager) {
  return getMissingDiscoveryLabOpeningAssets(textures).length === 0;
}
